import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminAccountDetail, AdminEntry, AdminRole } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { Button, Empty, Field, Input, Mono, Page, PageHeader, Panel, Select, Skeleton } from '@bounded/ui/primitives';
import { ErrorNotice } from '@bounded/ui/state';
import { useT } from '../../i18n';

export const Route = createFileRoute('/_app/admins')({ component: Admins });
const ROLES = ['support', 'operator', 'owner', 'none'] as const;

function Admins() {
  const t = useT(); const m = t.admins;
  const me = useMe();
  const admins = useQuery({ ...q.admins(), enabled: me.permissions.manage_admins });
  if (!me.permissions.manage_admins) return <Page><PageHeader title={m.title} /><p className="text-graphite">{m.ownerOnly}</p></Page>;
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <Panel>
        <dl className="grid gap-x-6 gap-y-2 px-4 py-4 sm:grid-cols-[max-content_minmax(0,1fr)]">
          {(['support', 'operator', 'owner'] as const).map((r) => [<dt key={`k${r}`} className="font-medium">{t.roles[r]}</dt>, <dd key={`v${r}`} className="text-graphite">{t.roleHelp[r]}</dd>])}
        </dl>
      </Panel>
      <GrantForm />
      {admins.error ? <ErrorNotice error={admins.error} onRetry={() => void admins.refetch()} /> : (
        <Panel>
          {!admins.data ? <div className="p-4"><Skeleton className="h-24" /></div> : !admins.data.items.length ? <Empty title={m.empty} /> : (
            <ul>{admins.data.items.map((a) => <AdminRow key={a.user_id} admin={a} self={a.user_id === me.user_id} />)}</ul>
          )}
        </Panel>
      )}
    </Page>
  );
}

function AdminRow({ admin, self }: { admin: AdminEntry; self: boolean }) {
  const t = useT(); const m = t.admins;
  return (
    <li className="grid gap-3 border-b border-rule px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="min-w-0">
          <Link to="/accounts/$accountId" params={{ accountId: admin.user_id }} className="font-medium hover:underline">{admin.name}</Link>
          {self && <span className="ml-2 text-sm text-graphite">{m.you}</span>}
          <span className="ml-3 text-graphite"><Mono value={admin.user_id} /></span>
        </p>
        <span className="font-medium">{t.roles[admin.role]}</span>
      </div>
      <RoleChange target={{ id: admin.user_id, name: admin.name, revision: admin.revision, role: admin.role }} />
    </li>
  );
}

function RoleChange({ target, label }: { target: { id: string; name: string; revision: number; role: AdminRole | null }; label?: string }) {
  const t = useT(); const m = t.admins;
  const queryClient = useQueryClient();
  const [role, setRole] = useState<AdminRole | 'none'>(target.role === 'owner' ? 'operator' : target.role ? 'owner' : 'support');
  return (
    <ReasonAction label={label ?? m.change} title={m.changeTitle(target.name)} consequence={<>{m.consequence} {t.accounts.recentLogin}</>}
      valid={role !== (target.role ?? 'none')} payload={{ user: target.id, role, expected_revision: target.revision }}
      run={(reason, key) => api.setAdminRole(target.id, { expected_revision: target.revision, role, reason }, key)}
      onDone={() => Promise.all(['admins', 'audit', 'account', 'me'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })))}>
      <Field label={m.newRole} htmlFor={`role-${target.id}`} hint={t.roleHelp[role]}>
        <Select id={`role-${target.id}`} value={role} onChange={(e) => setRole(e.target.value as AdminRole | 'none')} className="max-w-60">
          {ROLES.filter((r) => r !== (target.role ?? 'none')).map((r) => <option key={r} value={r}>{t.roles[r]}</option>)}
        </Select>
      </Field>
    </ReasonAction>
  );
}

/** Look an account up by id first: the role change needs its admin revision, and the operator should see whose it is. */
function GrantForm() {
  const t = useT(); const m = t.admins;
  const [id, setId] = useState('');
  const lookUp = useMutation({ mutationFn: (account: string) => api.account(account) });
  const found: AdminAccountDetail | undefined = lookUp.data?.account;
  return (
    <Panel title={m.grant}>
      <div className="grid gap-4 px-4 py-4">
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (id.trim()) lookUp.mutate(id.trim()); }}>
          <div className="w-full max-w-md"><Field label={m.account} htmlFor="grant-id" hint={m.accountHint}><Input id="grant-id" className="font-mono" value={id} onChange={(e) => { setId(e.target.value); lookUp.reset(); }} /></Field></div>
          <Button type="submit" disabled={!id.trim() || lookUp.isPending} className="mb-6">{m.lookUp}</Button>
        </form>
        {lookUp.error && <ErrorNotice error={lookUp.error} />}
        {found && (
          <div className="grid gap-3 rounded-[var(--radius-control)] border border-rule px-3 py-2.5">
            <p><span className="font-medium">{found.name}</span> <span className="text-graphite">{found.admin_role ? t.roles[found.admin_role] : t.roles.none}</span></p>
            <RoleChange key={`${found.id}/${found.revision}`} label={m.grant} target={{ id: found.id, name: found.name, revision: found.revision, role: found.admin_role }} />
          </div>
        )}
      </div>
    </Panel>
  );
}
